import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CellAlignmentEnum, ConfigColumn, EqpTableComponent, TypeColumn } from '@eqproject/eqp-table';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NotificationParameterDTO, NotificationTypeEnum } from '../../../models/notification-center/notification-parameter.model';
import { NotificationDetailDTO } from '../../../models/notification-center/notificationDetail.model';
import { UserDTO } from '../../../models/user.model';
import { AuthService } from '../../../services/auth.service';
import { DialogService } from '../../../services/dialog.service';
import { EventHandlerService } from '../../../services/eventHandler.service';
import { GlobalService } from '../../../services/global.service';
import { NotificationService } from '../../../services/notification-center/notification.service';
import { UtilityService } from '../../../services/utility.service';


@Component({
  selector: 'app-list-notifications',
  templateUrl: './list-notifications.component.html',
  styleUrls: ['./list-notifications.component.scss']
})
export class ListNotificationsComponent implements OnInit {


  notificationList: Array<NotificationDetailDTO> = new Array<NotificationDetailDTO>();
  columns: Array<ConfigColumn>;

  currentUser : UserDTO;

  @ViewChild('table', { static: false }) table: EqpTableComponent;
  @ViewChild("notificationTypeTemplate", {static: true}) notificationTypeTemplate : TemplateRef<any>;

  //#region  Dialog per la visualizzazione di una notifica
  dialogViewNotificationRef: MatDialogRef<TemplateRef<any>>;
  @ViewChild('dialogViewNotification', { static: false }) dialogViewNotification: TemplateRef<any>;
  selectedNotification: NotificationDetailDTO;
  //#endregion

  //#region Dialog aggiunta notifica
  notificationParameter : NotificationParameterDTO = new NotificationParameterDTO();
  notificationTypeEnum = NotificationTypeEnum;
  notificationTypeEnumArray = Array<any>();
  addNotificationForm : UntypedFormGroup;
  @ViewChild("dialogAddNotification") dialogAddNotification: TemplateRef<any>;
  dialogAddNotificationRef : MatDialogRef<TemplateRef<any>>;
  //#endregion

  notificationUpdateSubscription: Subscription;
  notificationReadSubscription: Subscription;

  constructor(
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private eventHandlerService: EventHandlerService,
    private translate: TranslateService,
    private authService : AuthService,
    private utilityService : UtilityService,
    private formBuilder : UntypedFormBuilder
  ) {
  }

  /**
   * Qui viene eseguito il subcribe agli eventi "NotificationUpdateEvent" (recupera l'elenco delle notifiche e aggiorna la tabella)
   * e "NotificationReadEvent" (segna come letta la notifica relativa all'ID ricevuto senza dover ricaricare i dati)
   */
  ngOnInit(): void {
    this.configureColumns();
    this.notificationTypeEnumArray = this.utilityService.GetEnumArray(NotificationTypeEnum, true,this.translate, "ENUMS.NOTIFICATION_TYPE.");
    this.loadNotification();
    this.notificationUpdateSubscription = this.eventHandlerService.subscribe(
      'NotificationUpdateEvent',
      (response) => { this.loadNotification(); }
    );
    this.notificationReadSubscription = this.eventHandlerService.subscribe(
      'NotificationReadEvent',
      (response) => {
        if (this.notificationList.find(n => n.ID == response.content.ID)) {
          let readNotification = this.notificationList.find(n => n.ID == response.content.ID);
          readNotification.ReadDate = response.content.ReadDate;
        }
      }
    );

    this.currentUser = this.authService.getCurrentUser();
    this.createForm();
  }

  ngOnDestroy(): void {
    if (this.notificationUpdateSubscription)
      this.notificationUpdateSubscription.unsubscribe();
    if (this.notificationReadSubscription)
      this.notificationReadSubscription.unsubscribe();
  }

  configureColumns() {
    this.columns = [
      {
        key: "action", display: "",
        type: TypeColumn.SimpleAction,
        actions: [
          { name: '', icon: "visibility", fn: (element, col, elementIndex) => this.readNotification(element) },
          { name: '', icon: 'delete', fn: (element) => this.deleteNotification(element), hidden: GlobalService.getUsedCompany() != 3}
        ],
        styles: { flex: "0 0 7%" }
      },
      { key: "NotificationTitle", display: this.translate.instant("GENERIC_FIELDS.TITLE"), styles: { flex: "0 0 25%" } },
      { key: "NotificationMessage", display: this.translate.instant("GENERIC_LABEL.MESSAGE"), styles: { flex: "0 0 43%" } },
      { key: 'NotificationNotificationType', display: this.translate.instant("GENERIC_LABEL.NOTIFICATION_TYPE"), styles: {flex: "0 0 5%", cellAlignment: CellAlignmentEnum.CENTER}, isSearchable: false, type: TypeColumn.ExternalTemplate, externalTemplate: this.notificationTypeTemplate},
      { key: "SendDate", display: this.translate.instant("GENERIC_FIELDS.DATE"), type: TypeColumn.Date, styles: { flex: "0 0 15%" }, format:"short" },
      { key: "IsRead", value: (element) => { return !!element.ReadDate }, display: this.translate.instant("GENERIC_LABEL.READ"), type: TypeColumn.Boolean, booleanValues: { true: '<i class="fa fa-check success-color"></i>',false: '' }, styles: { flex: "0 0 5%" } },
    ];
  }

  createForm(){
    this.addNotificationForm = this.formBuilder.group({
      title: [this.notificationParameter.Title, Validators.required],
      notificationType: [this.notificationParameter.NotificationType, Validators.required],
      message: [this.notificationParameter.Message, Validators.required],
      link: [this.notificationParameter.Link]
    })
  }

  loadNotification() {
    this.notificationService.getNotifications(false)
      .then(res => {
        this.notificationList = res;
        if (this.table)
          this.table.reloadDatatable();
      })
      .catch((err) => { DialogService.Error(err.message); });
  }

  readNotification(notification: NotificationDetailDTO) {
    if (!notification.ReadDate)
      this.notificationService.markAsRead(notification.ID)
        .then((res) => {
          notification.ReadDate = res;
          this.eventHandlerService.broadcast({ name: 'NotificationReadEvent', content: { ReadDate: res, ID: notification.ID } });
        })
        .catch((err) => { DialogService.Error(err.message); });
    this.selectedNotification = notification;
    this.dialogViewNotificationRef = this.dialog.open(this.dialogViewNotification, {
      disableClose: false,
      hasBackdrop: true,
      autoFocus: false,
      width: '40%',
    });
  }

  closeVisualizationModal(){
    this.dialogViewNotificationRef.close();
  }

  allNotifiesAsRead() {
    let notificationsToSend: Array<NotificationDetailDTO> = this.notificationList.filter(n => n.ReadDate == null);
    this.notificationService.markListAsRead(notificationsToSend.map(n => n.ID)).then(res => {
      notificationsToSend.forEach(notification => {
        this.eventHandlerService.broadcast({ name: 'NotificationReadEvent', content: { ReadDate: res, ID: notification.ID } });
      });
    }).catch(error => { DialogService.Error(error.message); });
  }

  openAddNotificationModal(){
    this.notificationParameter = new NotificationParameterDTO();
    this.notificationParameter.FK_Company = GlobalService.getUsedCompany();
    this.notificationParameter.IsSystemNotification = true;

    this.dialogAddNotificationRef = this.dialog.open(this.dialogAddNotification, {
      maxWidth: '80%',
      height: '50%',
      autoFocus: false,
      disableClose: false,
      hasBackdrop: true,
    })
  }

  addNotification(){
    DialogService.Confirm(this.translate.instant("GENERIC_LABEL.SEND_SYSTEM_NOTIFICATION_CONFIRM"), () => {
      this.notificationService.createNotification(this.notificationParameter).then(() => {
        this.closeAddNotificationModal();
        this.loadNotification();
        DialogService.Success(this.translate.instant("DIALOGS.NOTIFICATION_SUCCESS"));
      }).catch(err => {
        DialogService.Error(err.message);
      });
    }, true)
  }

  closeAddNotificationModal(){
    this.addNotificationForm.reset();
    this.dialogAddNotificationRef.close();
  }

  deleteNotification(element : NotificationDetailDTO){
    DialogService.Confirm(this.translate.instant("GENERIC_LABEL.DELETE_SYSTEM_NOTIFICATION_CONFIRM"), () => {
      this.notificationService.deleteNotification(element.FK_Notification).then(() => {
        this.loadNotification();
        DialogService.Success(this.translate.instant("DIALOGS.DELETE_SUCCESS"));
      }).catch(err => {
        DialogService.Error(err.message);
      });
    }, true)
  }

}
